import React, { useState, useEffect } from 'react';
import { typesService, HardwareType } from '../../services/typesService';
import { useI18n } from '../../contexts/I18nContext';
import SearchableSelect from './SearchableSelect';

interface DeviceTypeSelectorProps {
  value: string;
  onChange: (value: string) => void;
  id?: string;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  includeEmpty?: boolean;
  showDescription?: boolean;
  ariaLabel?: string;
}

/**
 * DeviceTypeSelector - A dropdown that fetches hardware types from the API
 *
 * Displays hardware types (PGA, P4GU, IOTL, TMS2) with their friendly names.
 * Fetches from /v2/hardware-types endpoint to get hardware type data.
 */
export const DeviceTypeSelector: React.FC<DeviceTypeSelectorProps> = ({
  value,
  onChange,
  id,
  className = '',
  placeholder = 'Select device type...',
  disabled = false,
  includeEmpty = true,
  showDescription = false,
  ariaLabel = 'Device type'
}) => {
  const { t } = useI18n();
  const [hardwareTypes, setHardwareTypes] = useState<HardwareType[]>([]);
  // Every type, inactive ones included: the current value may be one of them.
  const [allTypes, setAllTypes] = useState<HardwareType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchHardwareTypes = async () => {
      try {
        setLoading(true);
        setError(null);
        // Fetch with includeInactive=true and filter client-side rather than
        // relying on the server-side filter. DeviceTypesPage uses the same
        // source, so both surfaces always agree on the catalogue — eliminates
        // a class of drift where a UI list was missing a hardware type only
        // in some places. Sorted by name so the dropdown order is stable
        // across browsers.
        const all = await typesService.getHardwareTypes(true);
        const activeTypes = all
          .filter((t) => t.is_active)
          .sort((a, b) => (a.name || a.code).localeCompare(b.name || b.code));
        if (activeTypes.length === 0) {
          console.warn(
            '[DEVICE TYPE SELECTOR] No active hardware types returned from API. '
              + 'Dropdown will be empty. Check /v2/hardware-types access.',
            { fetched: all.length },
          );
        }
        setHardwareTypes(activeTypes);
        setAllTypes(all);
      } catch (err) {
        console.error('Failed to fetch hardware types:', err);
        setError('Failed to load hardware types');
        setHardwareTypes([]);
      } finally {
        setLoading(false);
      }
    };

    fetchHardwareTypes();
  }, []);

  if (loading) {
    return (
      <select id={id} className={className} disabled aria-label={ariaLabel}>
        <option>{t('deviceTypes.loadingHardwareTypes')}</option>
      </select>
    );
  }

  if (error && hardwareTypes.length === 0) {
    return (
      <select id={id} className={className} disabled aria-label={ariaLabel}>
        <option>{t('deviceTypeSelector.errorLoading')}</option>
      </select>
    );
  }

  const options = hardwareTypes.map((ht) => ({
    value: ht.code,
    label: `${ht.name || ht.code}${showDescription && ht.description ? ` - ${ht.description}` : ''}`,
  }));
  // A value on a deactivated type (an old template, say) still shows,
  // marked inactive, instead of an empty field that looks like a bug.
  if (value && !hardwareTypes.some((ht) => ht.code === value)) {
    const off = allTypes.find((ht) => ht.code === value);
    options.push({ value, label: `${off?.name || value} ${t('deviceTypes.inactiveSuffix')}` });
  }

  return (
    <SearchableSelect
      id={id}
      className={className}
      value={value}
      onChange={onChange}
      disabled={disabled}
      ariaLabel={ariaLabel}
      placeholder={placeholder}
      allowClear={includeEmpty}
      options={options}
    />
  );
};

interface DeviceTypeFilterProps {
  value: string;
  onChange: (value: string) => void;
  allLabel?: string;
  className?: string;
  ariaLabel?: string;
}

/**
 * DeviceTypeFilter - A filter dropdown for hardware types
 *
 * Similar to DeviceTypeSelector but always includes an "All" option.
 */
export const DeviceTypeFilter: React.FC<DeviceTypeFilterProps> = ({
  value,
  onChange,
  allLabel = 'All Device Types',
  className = '',
  ariaLabel = 'Filter by device type'
}) => {
  const [hardwareTypes, setHardwareTypes] = useState<HardwareType[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHardwareTypes = async () => {
      try {
        setLoading(true);
        const all = await typesService.getHardwareTypes(true);
        const activeTypes = all
          .filter((t) => t.is_active)
          .sort((a, b) => (a.name || a.code).localeCompare(b.name || b.code));
        setHardwareTypes(activeTypes);
      } catch (err) {
        console.error('Failed to fetch hardware types:', err);
        setHardwareTypes([]);
      } finally {
        setLoading(false);
      }
    };

    fetchHardwareTypes();
  }, []);

  if (loading) {
    return (
      <select className={className} disabled aria-label={ariaLabel}>
        <option>Loading...</option>
      </select>
    );
  }

  return (
    <select
      className={className}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={ariaLabel}
    >
      <option value="">{allLabel}</option>
      {hardwareTypes.map((ht) => (
        <option key={ht.code} value={ht.code}>
          {ht.name || ht.code}
        </option>
      ))}
    </select>
  );
};

export default DeviceTypeSelector;
